/**
 * Слайс registration-survey: статус опроса и meta (без формы опроса).
 *
 * @module store/registrationSurvey
 */

import { createSelector } from 'reselect';

import { FetchError } from '../../requests/sendRequest';
import type { RootAction } from '../store.types';
import type { MetaState } from '../store.utils';
import { setMeta } from '../store.utils';

/** Старт загрузки статуса опроса */
export const FETCH_REGISTRATION_SURVEY_START = 'FETCH_REGISTRATION_SURVEY_START';
/** Успешная загрузка */
export const FETCH_REGISTRATION_SURVEY_SUCCESS = 'FETCH_REGISTRATION_SURVEY_SUCCESS';
/** Ошибка загрузки */
export const FETCH_REGISTRATION_SURVEY_ERROR = 'FETCH_REGISTRATION_SURVEY_ERROR';

export const REGISTRATION_SURVEY_FETCH_ERROR_KEY = 'fetch';

/** Данные статуса: null означает «опрос не найден» (например 404) */
export interface RegistrationSurveyPayload {
  isNeedRunRegistrationSurvey: boolean;
}

export type FetchRegistrationSurveyStartAction = {
  type: typeof FETCH_REGISTRATION_SURVEY_START;
  payload: { token: string };
};

export type FetchRegistrationSurveySuccessAction = {
  type: typeof FETCH_REGISTRATION_SURVEY_SUCCESS;
  payload: RegistrationSurveyPayload | null;
};

export type FetchRegistrationSurveyErrorAction = {
  type: typeof FETCH_REGISTRATION_SURVEY_ERROR;
  payload: Record<string, unknown>;
};

export type RegistrationSurveyAction =
  | FetchRegistrationSurveyStartAction
  | FetchRegistrationSurveySuccessAction
  | FetchRegistrationSurveyErrorAction;

export interface RegistrationSurveyState {
  registrationSurvey: RegistrationSurveyPayload | null;
  meta: MetaState;
}

export const initialRegistrationSurveyState: RegistrationSurveyState = {
  registrationSurvey: null,
  meta: setMeta.initial(),
};

/**
 * Редьюсер registration-survey.
 *
 * @param {RegistrationSurveyState} state — состояние
 * @param {RootAction} action — корневой экшен
 * @returns {RegistrationSurveyState}
 */
export function registrationSurveyReducer(state: RegistrationSurveyState, action: RootAction): RegistrationSurveyState {
  if (action.type === FETCH_REGISTRATION_SURVEY_START) {
    return { ...state, meta: setMeta.pending() };
  }
  if (action.type === FETCH_REGISTRATION_SURVEY_SUCCESS) {
    return {
      ...state,
      registrationSurvey: action.payload,
      meta: setMeta.success(),
    };
  }
  if (action.type === FETCH_REGISTRATION_SURVEY_ERROR) {
    return { ...state, meta: setMeta.error(action.payload) };
  }
  return state;
}

export const selectRegistrationSurveyMeta = (state: RegistrationSurveyState) => state.meta;

export const selectRegistrationSurveyData = (state: RegistrationSurveyState) => state.registrationSurvey;

export const selectFetchRegistrationSurveyStatusComplete = createSelector(
  [selectRegistrationSurveyMeta],
  (meta) => !meta.pending && (meta.success || Object.keys(meta.errors).length > 0),
);

export const selectFetchRegistrationSurveyStatusLoading = createSelector(
  [selectRegistrationSurveyMeta],
  (meta) => meta.pending,
);

export const selectFetchRegistrationSurveyStatusErrored = createSelector(
  [selectRegistrationSurveyMeta],
  (meta) => Object.keys(meta.errors).length > 0,
);

export const selectRegistrationSurveyError = createSelector(
  [selectRegistrationSurveyMeta],
  (meta): FetchError | null => {
    if (!Object.hasOwn(meta.errors, REGISTRATION_SURVEY_FETCH_ERROR_KEY)) {
      return null;
    }
    const candidate = meta.errors[REGISTRATION_SURVEY_FETCH_ERROR_KEY];
    if (candidate instanceof FetchError) {
      return candidate;
    }
    return null;
  },
);

export const selectRegistrationSurveyIsFilled = createSelector(
  [
    selectFetchRegistrationSurveyStatusLoading,
    selectFetchRegistrationSurveyStatusComplete,
    selectRegistrationSurveyData,
  ],
  (loading, complete, data) =>
    !loading && complete && data !== null && typeof data === 'object' && Object.keys(data).length > 0,
);

/**
 * Опрос на стороне API считается пройденным: успешный GET и `isNeedRunRegistrationSurvey === false`.
 */
export const selectRegistrationSurveyCompleted = createSelector(
  [
    selectFetchRegistrationSurveyStatusLoading,
    selectFetchRegistrationSurveyStatusComplete,
    selectFetchRegistrationSurveyStatusErrored,
    selectRegistrationSurveyData,
  ],
  (loading, complete, errored, data) => {
    if (loading || !complete || errored) {
      return false;
    }
    if (data === null || typeof data !== 'object') {
      return false;
    }
    if (!Object.hasOwn(data, 'isNeedRunRegistrationSurvey')) {
      return false;
    }
    return data.isNeedRunRegistrationSurvey === false;
  },
);

/**
 * Нужно показать форму опроса: успешный GET без ошибок и (404 как `null` или `isNeedRunRegistrationSurvey === true`).
 *
 * @returns {boolean}
 */
export const selectRegistrationSurveyNeeded = createSelector(
  [
    selectFetchRegistrationSurveyStatusLoading,
    selectFetchRegistrationSurveyStatusComplete,
    selectFetchRegistrationSurveyStatusErrored,
    selectRegistrationSurveyData,
  ],
  (loading, complete, errored, data) => {
    if (loading || !complete || errored) {
      return false;
    }
    if (data === null) {
      return true;
    }
    if (typeof data !== 'object') {
      return false;
    }
    if (!Object.hasOwn(data, 'isNeedRunRegistrationSurvey')) {
      return false;
    }
    return data.isNeedRunRegistrationSurvey === true;
  },
);

/**
 * Успешное завершение GET, но тело ответа не содержит ожидаемого булева флага — сценарий gate не определён, нужен fallback redirect.
 *
 * @returns {boolean}
 */
export const selectRegistrationSurveyResponseMalformed = createSelector(
  [
    selectFetchRegistrationSurveyStatusLoading,
    selectFetchRegistrationSurveyStatusComplete,
    selectFetchRegistrationSurveyStatusErrored,
    selectRegistrationSurveyData,
  ],
  (loading, complete, errored, data) => {
    if (loading || !complete || errored) {
      return false;
    }
    if (data === null) {
      return false;
    }
    if (typeof data !== 'object') {
      return true;
    }
    if (!Object.hasOwn(data, 'isNeedRunRegistrationSurvey')) {
      return true;
    }
    const flag = data.isNeedRunRegistrationSurvey;
    return typeof flag !== 'boolean';
  },
);
